import templateUrl from './modelPicker.html';
import modelPickerModal from './modelPickerModal/modelPickerModal';
import './modelPicker.less';

/**
 * @ngdoc directive
 * @name eventix.common.directives:modelPicker
 * @restrict element
 * @description
 * A model picker to pick one or more event(s), (nested) ticket(s), (nested) product(s)
 *
 * @example <pre>
 *     <model-picker output-model="Ticket" parent-model="$ctrl.shop" multiple reorder lock-event on-pick="$ctrl.returnFunction(events, tickets)" event="$ctrl.event" events="$ctrl.selectedEvents" tickets="$ctrl.selectedTickets" on-dismiss="$ctrl.dismissed()" button-class="btn btn-primary" button-text="Custom text"></model-picker>
 * </pre>
 *
 * @param {String} outputModel The model-name(s) to pick
 * @param {BaseClass} parentModel The parent model (for ordering)
 * @param {Event} event The currently selected event (When outputModel is 'Event')
 * @param {Ticket} ticket The currently selected ticket (When outputModel is 'Ticket')
 * @param {Product} product The currently selected product (When outputModel is 'Product')
 * @param {Shop} shop The currently selected shop (When outputModel is 'Shop')
 * @param {Array} events The currently selected events (one-way binding)
 * @param {Array} tickets The currently selected tickets (one-way binding)
 * @param {Array} products The currently selected products (one-way binding)
 * @param {Array} shops The currently selected shops (one-way binding)
 * @param {Object} forcedFilters Any filters that should be superimposed on the data calls (one-way binding)
 * @param {Expression} onPick A callback for when the selection is finished, the (optional) arguments contain the selected value(s)
 * @param {Expression} onDismiss A callback for when the selection is cancelled, the (optional) arguments contain the selected value(s) at the time of cancellation
 * @param {Expression} afterSort A callback for when sorting is finished
 * @param {Boolean} multiple Flag for choosing one item or multiple
 * @param {Boolean} reorder Flag to indicate whether items should be orderable (only when multiple is set)
 * @param {Boolean} list Flag to indicate whether items should be visible as a list (only when multiple is set)
 * @param {Boolean} lockEvent Lock the event, choices for outputModel will be scoped to only one event (When switching to another event, the current selection is removed).
 * @param {String} buttonClass Overwrite class on button
 * @param {String} buttonText Overwrite button text
 */
export default angular.module('eventix.common.directives.modelPicker', [modelPickerModal])
    .component('modelPicker', {
        templateUrl: templateUrl,
        controller: ModelPickerController,
        bindings: {
            outputModel: '@',
            parentModel: '<?',
            event: '=?',
            ticket: '=?',
            product: '=?',
            shop: '=?',
            events: '<?',
            tickets: '<?',
            products: '<?',
            shops: '<?',
            forcedFilters: '<?',
            onPick: '&?',
            onDismiss: '&?',
            afterSort: '&?',
            buttonText: '@?',
            buttonClass: '@?',
        },
    }).name;

function ModelPickerController($uibModal, $attrs, $scope, $q, ErrorRejector, sortableCallbacks, Event, EventDate) {
    const $ctrl = this;

    const DEFAULT_BUTTON_CLASS = 'mv20 height-2-lines w100';
    const OUTPUTMODELS = ['Event', 'Ticket', 'Product', 'Shop'];
    const sortCallback = sortableCallbacks.onSort(onSortCallback);

    $ctrl.outputModelCount = 0;
    $ctrl.showReorderList = false;
    $ctrl.showList = false;
    $ctrl.busy = false;
    $ctrl.sortable = {
        animation: 150,
        get disabled() {
            return $ctrl.busy || !$ctrl.parentModel;
        },
        set disabled(v) {
            return v;
        },
        onSort: onSort,
    };

    $ctrl.openPicker = openPicker;
    $ctrl.toggleList = toggleList;
    $ctrl.toggleReorderList = toggleReorderList;
    $ctrl.detachChild = detachChild;
    $ctrl.upsertOutputSingle = _.throttle(upsertOutputSingle, 250);
    $ctrl.upsertOutputMultiple = _.throttle(upsertOutputMultiple, 250);
    $ctrl.forcedFilters = {};
    $ctrl.onPick = null;
    $ctrl.onDismiss = null;
    $ctrl.afterSort = angular.noop;

    $ctrl.sortQueue = $q.resolve();

    $ctrl.$onInit = function() {
        if (_.indexOf(OUTPUTMODELS, $ctrl.outputModel) < 0) {
            return ErrorRejector.handle('Invalid output model', $ctrl.outputModel);
        }

        $ctrl.multiple = _.has($attrs, 'multiple') && _.get($attrs, 'multiple', 'false') !== 'false';

        $ctrl.reorder = _.every([
            $ctrl.multiple,
            _.has($attrs, 'reorder') && _.get($attrs, 'reorder', 'false') !== 'false',
            $ctrl.parentModel && typeof $ctrl.parentModel['$reorder' + $ctrl.outputModel] === 'function',
        ]);

        $ctrl.list = _.every([
            $ctrl.multiple,
            !$ctrl.reorder,
            _.has($attrs, 'list') && _.get($attrs, 'list', 'false') !== 'false',
        ]);

        $ctrl.lockEvent = _.has($attrs, 'lockEvent') && _.get($attrs, 'lockEvent', 'false') !== 'false';

        $ctrl.autoButtonText = !_.has($attrs, 'buttonText');

        if (!_.has($attrs, 'buttonClass')) {
            $ctrl.buttonClass = DEFAULT_BUTTON_CLASS;
        }

        if (!$ctrl.outputModel) {
            throw Error('Output model is required for this component to work');
        }

        if ($ctrl.lockEvent && $ctrl.outputModel === 'Event') {
            throw Error('When locking the event, the output model can not be the event.');
        }

        return $ctrl.selected = {
            Event: $ctrl.events,
            Ticket: $ctrl.tickets,
            Product: $ctrl.products,
            Shop: $ctrl.shops,
        };
    };

    $scope.$watchGroup(['$ctrl.events', '$ctrl.tickets', '$ctrl.products', '$ctrl.shops'], $ctrl.upsertOutputMultiple);
    $scope.$watchGroup(['$ctrl.event', '$ctrl.ticket', '$ctrl.product', '$ctrl.shop'], $ctrl.upsertOutputSingle);

    function upsertOutputMultiple() {
        $ctrl.buttonText = false;

        $ctrl.outputModelCount = _.get($ctrl, _.lowerCase($ctrl.outputModel) + 's.length', 0);

        updateEventLabelForReorder();

        $ctrl.selected = {
            Event: $ctrl.events,
            Ticket: $ctrl.tickets,
            Product: $ctrl.products,
            Shop: $ctrl.shops,
        };
    }

    function upsertOutputSingle() {
        $ctrl.buttonText = $ctrl.multiple ? false : _.get($ctrl, _.lowerCase($ctrl.outputModel) + '.name', false);

        if (!$ctrl.buttonText) {
            $ctrl.outputModelCount = _.get($ctrl, _.lowerCase($ctrl.outputModel), false) ? 1 : 0;
        }
    }

    function updateEventLabelForReorder() {
        if ($ctrl.reorder || $ctrl.list) {
            // We will not make separate calls to retrieve the event names...
            // So only if the events are cached, we will show the event labels
            const firstEventDates = _.mapValues(_.groupBy(EventDate.cached, 'event_id'), eventDates => {
                if (_.isEmpty(eventDates)) {
                    return null;
                }

                let first = _.minBy(eventDates, 'start');

                if (!first) {
                    return null;
                }

                return moment(_.get(first, 'start')).format('YYYY-MM-DD');
            });

            const injectEventKey = function(model) {
                let eventName = _.get(Event.cached, [model.event_id, 'name'], '?');
                let eventStart = _.get(firstEventDates, model.event_id);

                if (eventName && eventStart) {
                    eventName = `${eventName} (${eventStart})`;
                }

                model.event_key = eventName;
            };

            _.forEach($ctrl.tickets, injectEventKey);
            _.forEach($ctrl.products, injectEventKey);
        }
    }

    function openPicker() {
        $ctrl.showReorderList = false;
        $ctrl.showList = false;

        return $uibModal.open({
            component: 'modelPickerModal',
            resolve: {
                outputModel: () => $ctrl.outputModel,
                multiple: () => $ctrl.multiple,
                lockEvent: () => $ctrl.lockEvent,
                events: () => $ctrl.events,
                tickets: () => $ctrl.tickets,
                products: () => $ctrl.products,
                shops: () => $ctrl.shops,
                event: () => $ctrl.event,
                forcedFilters: () => $ctrl.forcedFilters,
                // TODO Does this need ticket, product and/or shop ???
            },
            size: 'lg',
        }).result.then(returnSuccessHandler, $ctrl.onDismiss || angular.noop);
    }

    function returnSuccessHandler(values) {
        if ($ctrl.multiple) {
            $ctrl.events = values.events;
            $ctrl.tickets = values.tickets;
            $ctrl.procucts = values.products;
            $ctrl.shops = values.shops;
        } else {
            switch ($ctrl.outputModel) {
                case 'Ticket':
                    $ctrl.ticket = values.ticket;
                    break;
                case 'Product':
                    $ctrl.product = values.product;
                    break;
                case 'Shop':
                    $ctrl.shop = values.shop;
                    break;
                case 'Event':
                default:
                    $ctrl.event = values.event;
                    break;
            }
        }

        if (typeof $ctrl.onPick === 'function') {
            return $q.resolve($ctrl.onPick.call(null, values));
        }

        return $q.resolve(values);
    }

    function toggleList() {
        if (!$ctrl.list) {
            $ctrl.showList = false;

            return ErrorRejector.handle('common.model-picker.notice.listUnavailable');
        }

        return $q.resolve($ctrl.showList = !$ctrl.showList);
    }

    function toggleReorderList() {
        if (!$ctrl.reorder) {
            $ctrl.showReorderList = false;

            return ErrorRejector.handle('common.model-picker.notice.reorderUnavailable');
        }

        return $q.resolve($ctrl.showReorderList = !$ctrl.showReorderList);
    }

    function detachChild(child) {
        if (!$ctrl.multiple) {
            return ErrorRejector.handle('common.model-picker.notice.cannotDetachSingle');
        }

        let returnValues = {outputModel: $ctrl.outputModel};

        _.forEach($ctrl.selected, (selected, model) => {
            selected = _.cloneDeep(selected);

            if (model === $ctrl.outputModel) {
                _.remove(selected, {guid: child.guid});
            }

            _.set(returnValues, _.lowerCase(model) + 's', selected);
        });

        return $q.resolve(returnSuccessHandler(returnValues));
    }

    function onSort(sortEvent) {
        if (!$ctrl.reorder) {
            return ErrorRejector.handle('Can not sort without properly initialized reordering');
        }

        return $q.resolve(sortCallback(sortEvent))
            .then(tickets => {
                $ctrl.afterSort(tickets);

                return tickets;
            });
    }

    function onSortCallback(pre, suc) {
        if (!($ctrl.sortQueue instanceof $q)) {
            return ErrorRejector.handle('Sort queue not available');
        }

        if (!_.isNil($ctrl.parentModel.guid)) {
            let promise = $ctrl.sortQueue
                .then(() => {
                    return $ctrl.parentModel['$reorder' + $ctrl.outputModel](pre, suc)
                        .catch(error => {
                            $ctrl.sortQueue = $q.resolve();

                            ErrorRejector.handle(error);

                            return $q.reject(error);
                        });
                });

            $ctrl.sortQueue = promise;

            return promise;
        }

        return $q.resolve();
    }
}
